import { Button, Spin, message, Popconfirm, Collapse, Checkbox, Modal, Row, Table, TableProps, notification, Tooltip, Space } from "antd";

import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { AppContext } from "../../context/app-context";
import { Directory, HttpPostDirectory, TagTableData } from "../../models/countries-interface";
import EditServiceDirectoryPage from "./EditServiceDirectoryPage";
import { CountrySelect } from "../../components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DirectoryApi } from "../../apis/directory-api";
import { TagApi } from "../../apis/tag-api";
import { BoxTag } from "../../components/box-tag/BoxTag";
import { resolveIcon } from "../../utils/api";
import { IconTag } from "../../components/icon-tag/IconTag";

import './ServiceDirectory.scss';
import './LocationResult.scss';

interface ServiceDirectoryPageProps {
  isNationalHelplines: boolean;
}

const ServiceDirectoryPage = ({isNationalHelplines}: ServiceDirectoryPageProps) => {
  const [directories, setDirectories] = useState<any[]>([]);
  const { selectedCountry, countries, setSelectedCountry, profile, isLoadingCountry } = useContext(AppContext);
  const [ editingDirectory, setEditingDirectory ] = useState<HttpPostDirectory|null>(null);
  const [ selectedServices, setSelectedServices ] = useState<string[]>([]);
  const [ selectedTags, setSelectedTags ] = useState<string[]>([]);
  const [ isLoading, setLoading ] = useState<boolean>(true);
  const [ tagEditingMode, setTagEditingMode ] = useState<boolean>(false);
  const [ isTagModalOpen, setIsTagModalOpen ] = useState<boolean>(false);
  const [ isTagModalCancel, setIsTagModalCancel ] = useState<boolean>(false);
  const [ isTagEditModeExitModalOpen, setIsTagEditModeExitModalOpen ] = useState<boolean>(false);

  const [api, contextHolder] = notification.useNotification();

  const [ isLoadingBulkAssignment, setIsLoadingBulkAssignment ] = useState<boolean>(false);
  const [ isLoadingTags, setIsLoadingTags ] = useState<boolean>(false);
  const [ tagData, setTagData ] = useState<TagTableData[] | null>(null);

  const loadDirectories = useCallback((c: string) => {
    setLoading(true);
    if (profile?.Market === "US" && !isNationalHelplines) return;

	DirectoryApi.getServices(c, isNationalHelplines)
	  .then((services) => {
        setDirectories(services);
        setLoading(false);
	  })
  }, [setLoading, setDirectories, isNationalHelplines, profile]);

  const refreshDirectories = useCallback(() => {
    if (profile !== null && countries !== null && selectedCountry !== null) {
      if (profile.Market === "All" && selectedCountry.code.iv === "US" && !isNationalHelplines) {
	    setSelectedCountry(countries[0]);
	    loadDirectories(countries[0].code.iv);
	  } else {
	    loadDirectories(selectedCountry.code.iv);
	  }
    }
  }, [profile, countries, selectedCountry, isNationalHelplines, setSelectedCountry, loadDirectories]);

  useEffect(() => {
    refreshDirectories();
  }, [refreshDirectories]);
    
  const onEdit = (directory: Directory) => {
    if (selectedCountry !== null) {
	  let err = false;
	  if (!directory.id) err = true;
	  if (!directory.country || directory.country.iv !== selectedCountry.code.iv) err = true;
	  if (!directory.isNationalHelpLine || directory.isNationalHelpLine.iv !== isNationalHelplines) err = true;

	  if (err) return;

	  let edit_dir = {
	    id: { iv: directory.id },
		isNationalHelpLine: { iv: isNationalHelplines },
		country: { iv: selectedCountry.code.iv },
		tags: { iv: directory.tags?.iv || [] },

		website: { iv: directory.website?.iv || "" },
		displayOnMap: { iv: directory.displayOnMap?.iv || false },
				
		order: { iv: directory.order?.iv || null },
		
		location: { iv: directory.location?.iv || { longitude: 0, latitude: 0 } },
		
		phoneNumbers: { iv: [...(directory.phoneNumbers?.iv || [])] },
		emailAddresses: { iv: [...(directory.emailAddresses?.iv || [])] },
		
		name: {...directory.name},
		hours: {...directory.hours},
		description: {...directory.description},
		address: {...directory.address},
	  };

	  for (let i = 0; i < selectedCountry.languages.iv.length; i++) {
	    let lang_code = selectedCountry.languages.iv[i].code;

		if (!edit_dir.name[lang_code]) edit_dir.name[lang_code] = "";
		if (!edit_dir.hours[lang_code]) edit_dir.hours[lang_code] = "";
		if (!edit_dir.description[lang_code]) edit_dir.description[lang_code] = "";
		if (!edit_dir.address[lang_code]) edit_dir.address[lang_code] = "";
	  }

	  setEditingDirectory(edit_dir);
	  resetTagEditingMode();
	}
  };

  const resetTagEditingMode = () => {
	setSelectedServices([]);
	setSelectedTags([]);
	setTagEditingMode(false);
  };

  const onDelete = (idToDelete: string, serviceName: string) => {
	setLoading(true);

	if (!serviceName) {
	  serviceName = isNationalHelplines ? "Helpline" : "Service";
	}

    DirectoryApi.deleteService(idToDelete)
	  .then(() => {
	    refreshDirectories();
	    message.info(`${serviceName} has been deleted.`);
	  })
	  .catch(() => {
        message.error(`An error was encountered: ${serviceName} has not been deleted.`);
	    setLoading(false);
	  });
  };

  const onAddNew = () => {
    if (selectedCountry !== null) {
	  let empty_lang_fields:{ [lang: string]: string } = {};

	  for (let i = 0; i < selectedCountry.languages.iv.length; i++) {
	    empty_lang_fields[selectedCountry.languages.iv[i].code] = "";
	  }

	  setEditingDirectory({
	    id: {iv: null},
	    country: { iv: selectedCountry.code.iv },
	    tags: { iv: [] },
	    isNationalHelpLine: { iv: isNationalHelplines },
	    order: { iv: null },

	    name: {...empty_lang_fields},
	    hours: {...empty_lang_fields},
	    description: {...empty_lang_fields},
	    address: {...empty_lang_fields},

	    website: { iv: "" },
	    phoneNumbers: { iv: [] },
	    emailAddresses: { iv: [] },

	    location: { iv: { longitude: 0, latitude: 0 }},
	    displayOnMap: { iv: false },				
	  });

	  resetTagEditingMode();
    }
  };

  const saveDirectory = () => {
    if (selectedCountry?.code.iv) {
      loadDirectories(selectedCountry?.code.iv);
    }
    setEditingDirectory(null);
  };

  const loadTags = () => {
	setIsLoadingTags(true);

	TagApi.getTags()
	  .then((tags: TagTableData[]) => { 

		setTagData(tags);
        setIsLoadingTags(false); 
      });
  };

  const openError = (message: string) => {
    api.error({
      message,
      description: null,
      placement: "top",
	  duration: 5000
    });
  };

  const openSuccess = (message: string) => {
    api.success({
      message,
      description: null,
      placement: "top",
    });
  };

  const submitBulkTagAssignment = () => {
	if (isNationalHelplines) {
	  return;
	}

	setIsLoadingBulkAssignment(true);
	DirectoryApi.bulkAssignTags({
	  tagIds: selectedTags,
	  locationIds: selectedServices,
	})
	  .then((result) => {
	    setIsLoadingBulkAssignment(false);
		if (result.length === selectedServices.length) {
		  openSuccess("Filters have been assigned successfully.");

		  resetTagEditingMode()
		  refreshDirectories();
		} else {
		  openError(`The following services were not updated successfully, please review them to correct the changes: ${result.map((x: { data: any }) => x.data.Name["en"]).join(",")}`);
		}
		setIsTagModalOpen(false);
	  })
	  .catch((error) => {
	    setIsLoadingBulkAssignment(false);
		console.log(error);
		openError('This bulk assignment has failed.');
		setIsTagModalOpen(false);
	  })
  };

  useEffect(() => {
    if (!tagData && !isLoadingTags) {
      loadTags();
    }
  }, [isLoadingTags, tagData]);

  const tagOptions = useMemo(() => {
    if (selectedCountry && tagData) {
      var filteredTags = tagData.filter(x => x.SupportedCountries.iv?.flatMap((x: any) => x.Data.Code).includes(selectedCountry?.code?.iv));
      return filteredTags.map((tag) => {
        return { 
		  label: 
		    tag.Type.iv === "Location" ?
			<IconTag key={tag.Id} text={tag.Title["en"]} icon={resolveIcon(tag.Icon?.iv)} /> :
			<BoxTag
			  key={tag.Id + "-bulk-option"}
			  text={tag.Title["en"]}
			  color={tag.ColorCode?.iv}
			  borderColor={tag.BorderColorCode?.iv}
		    />, 
		  value: tag.Id };
      });
    }

    return [];
  }, [selectedCountry, tagData]);

  const tagAssignmentPreviewColumns: TableProps<any>['columns'] = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    render: (text) => <span>{text["en"]}</span>,
  },
  {
    title: 'Filters',
    key: 'tags',
    dataIndex: 'tags',
    render: (tagIds: { iv: string[] }, record) => {
	  var newTagList: TagTableData[] = tagData?.filter(x => selectedTags?.includes(x.Id) || tagIds.iv?.includes(x.Id)) ?? [];

	  if (newTagList.length > 0) {
		return (
		  <div>
		    { newTagList.map(tag => {
		      return (
				<>
				  { tag.Type.iv === "Location" && (
			        <IconTag 
				      key={record.id + "-" + tag.Id} 
					  text={tag.Title["en"]} 
					  icon={resolveIcon(tag.Icon?.iv)} 
					/>
				  )}
				  { tag.Type.iv !== "Location" && (
			        <BoxTag
				      key={record.id + "-" + tag.Id}
			          text={tag.Title["en"]}
				      color={tag.ColorCode?.iv}
				      borderColor={tag.BorderColorCode?.iv}
			        />
				  )}
				</>
			  );
		    })}
		  </div>
		);
	  }
    },
  },
];

  const pageTitle = isNationalHelplines ? "National Helplines" : "Service Directory";
  
  if (profile?.Market === "United States" && !isNationalHelplines) return <></>;

  return (
	<>
      { !editingDirectory && 
		<>
		  { contextHolder } {/* notifications context */}
          <div className="content-header">
		    <h2>{pageTitle}</h2>
			{ !isLoading && directories && <span>({directories.length} locations)</span>}
			<span className="row-right"></span>
			{ profile?.Market === "All" && 
			  <>
			    <span>{'Country / Market:'}</span>
				<CountrySelect 
				  countryFilter={isNationalHelplines ? [] : ["United States"]}
				  onChange={() => {
					if (selectedServices.length > 0 || selectedTags.length > 0) {
					  setIsTagEditModeExitModalOpen(true);
					  return false;
					}
					return true;
				  }}
				/>
			  </>
		    }
			<Button 
			  onClick={onAddNew}
			  type="primary" 
			  icon={<FontAwesomeIcon icon={['far', 'plus']} />}
			>
			  {`New ${isNationalHelplines ? "National Helpline" : "Service"}`}
			</Button>
		  </div>
		  {!isNationalHelplines && (
		    <div className="bulk-actions-row">
			  <Collapse 
			    className="bulk-action-collapse"
			    ghost
			    activeKey={tagEditingMode ? "1" : undefined}
				onChange={() => {
				  if (selectedServices.length > 0 || selectedTags.length > 0) {
					setIsTagEditModeExitModalOpen(true); // confirm whether user was trying to exit tag edit mode
				  }
				  else {
					setTagEditingMode(prevState => !prevState);
				  }
				}}
			    items={[{
				  key: "1",
				  label: ( 
				    <Row>
				      <span>
					    <FontAwesomeIcon icon={["fas", "tags"]} />
					    {'Click here to assign filters to several services at once.'}
					  </span>
				    </Row>
				  ),
				  children: (
				    <>
				      { (isLoadingCountry || isLoadingTags) && (
			            <div className="center-box">
				          <Spin size="large" />
				        </div>
			          )}
				      { (!isLoadingCountry || !isLoadingTags) && (
					    <>
			              <div className="tag-group">
				            <div className="tag-container">
				              <h3>
							    <Space size={"small"}>
								  {"Available Filters"}
								  <Tooltip 
								    title={
							          <div>
									    {"You can request to add a new filter category or delete an existing filter category by creating a ticket via the Bright Sky Help Desk. The ticket must clearly state any new category titles in both English AND in your current market languages."}
									    <br/>
								        <br/>
									    {"Once a member of the Bright Sky team has confirmed your request, return to this section of the portal to see your requested changes."}
									  </div>
								    }
								    placement="right"
							 	  >
								    <FontAwesomeIcon id="available-filters-tooltip" icon={["far", "circle-info"]} />
								  </Tooltip>
						        </Space>
							  </h3>
					          <span className="tag-group-description">
							    { tagOptions.length === 0 ? 
								  "There are currently no filters available to assign, please see the \"i\" button for further information." : 
						          <div>
								    {"To assign a filter to a service;"}
								    <ul>
								      <li>{"Select from the available filters."}</li>
								      <li>{"Select services you wish to assign the filter to."}</li>
								      <li>{"Select \"Assign filters\"."}</li>
								    </ul>
								  </div>
							    }
							  </span>
					          <Checkbox.Group 
					            options={tagOptions} 
						        onChange={(checkedValue) => {setSelectedTags(checkedValue)}} 
						        value={selectedTags}
					          />
					        </div>
					        <Button
					          onClick={() => setIsTagModalOpen(true)}
					          type="primary"
				              disabled={isLoadingCountry || isLoadingTags || selectedTags.length === 0 || selectedServices.length === 0}
					        >
				              {'Assign Filters'}
					        </Button>
				          </div>
						  { tagOptions.length > 0 && (
						    <Checkbox
						      checked={selectedServices.length === directories.length}
						      onChange={(e) => setSelectedServices(e.target.checked ? directories.map(x => x.id) : [])}
					          indeterminate={selectedServices.length > 0 && selectedServices.length < directories.length}
						    >
						      {`Select all ${isNationalHelplines ? "Helplines" : "Services"}`}
						    </Checkbox>
						  )}
				        </>
				      )}
				    </>
				  )
			    }]}
		      />

			  {/* Tag Assignment Modals */}
			  <Modal
			    title="Assign the following filters?"
			    closable={{ 'aria-label': 'Custom Close Button' }}
			    open={isTagModalOpen}
			    onOk={() => submitBulkTagAssignment()}
			    onCancel={() => {
				  setIsTagModalCancel(true);
				  setIsTagEditModeExitModalOpen(true);
			    }}
			    width={800}
			    okButtonProps={{
				  loading: isLoadingBulkAssignment
		        }}
			    okText="Save Changes"
			  >
			    <p>{"Please confirm that you would like to save these changes. By clicking save you are updating the live Bright Sky systems. Please ensure all information you have added is accurate and correct."}</p>
			    <Table 
			      columns={tagAssignmentPreviewColumns} 
				  dataSource={directories.filter(x => selectedServices.includes(x.id))}
			 	  scroll={{ y: 400 }}
		        />
		      </Modal>
			  <Modal
			    style={{ top: isTagModalCancel ? 200 : undefined }}
			    title={isTagModalCancel ? "Are you sure you want to leave?" : "Are you sure you want to exit filter assignment?"}
			    closable={{ 'aria-label': 'Custom Close Button' }}
			    open={isTagEditModeExitModalOpen}
			    onOk={() => {
				  if (isTagModalCancel) {
			        setIsTagModalOpen(false);
				    // allow close transition to complete before resetting
				    setTimeout(() => {
					  setIsTagModalCancel(false);
				    }, 200);
				  } else {
				    setTagEditingMode(false);
				    setSelectedServices([]);
				    setSelectedTags([]);
				  }
				
				  setIsTagEditModeExitModalOpen(false);
			    }}
			    onCancel={() => { 
				  if (isTagModalCancel) {
				    // allow close transition to complete before resetting
				    setTimeout(() => {
					  setIsTagModalCancel(false);
				    }, 200);
				  }

			      setIsTagEditModeExitModalOpen(false);
			    }}
			  >
			    <p>{isTagModalCancel ? "Are you sure you want to leave? Leaving this page will not save any changes." : "Are you sure you want to exit filter assignment? This will clear your currently selected filters and services. No changes will be saved."}</p>
			  </Modal>
		    </div>
		  )}
	      
		  <div className="content-body directory-body">
		    { isLoading && (
              <div className="center-box">
			    <Spin size="large" />
			  </div>
            )}
		    { !isLoading && directories.map((r, i) => (
		      <div key={r.id} className="content-tile directory-tile">
				<Checkbox
					checked={selectedServices.includes(r.id)}
					style={{visibility: tagEditingMode && tagOptions.length > 0 ? 'visible' : 'hidden'}}
					onChange={(e) => {
					  if (e.target.checked) {
						setSelectedServices(prevState => { return prevState.concat(r.id) })
					  } else {
						setSelectedServices(prevState => { return prevState.filter(x => x !== r.id) })
					  }
					}}
				/>
			    <div className="directory-content">
			      <div className="row directory-header">
				    <div className="left">
				      <h2>{(r.name && r.name['en']) ? r.name['en'] : ""}</h2>
				      <div className="directory-desc" dangerouslySetInnerHTML={{ __html: (r.description && r.description['en']) ? r.description['en'] : "" }}></div>
				    </div>	
				    <div className="right">
	 			      <Button
				        aria-label='Edit'
				        onClick={() => onEdit(r)}
				        className="row-right"
				        type="default"
				        icon={<FontAwesomeIcon icon={['far', 'pen-to-square']} />}
				      >
				        {"Edit"}
				      </Button>
                      <Popconfirm
					    okText="Yes"
				        cancelText="No"
				        title={
				          <div style={{"display": "flex", "flexDirection": "column"}}>
				            <span>{`You are about to permanently delete this ${isNationalHelplines ? "helpline" : "service"} from all Bright Sky databases.`}</span>
				            <span>{`Are you sure you want to remove this ${isNationalHelplines ? "helpline" : "service"}?`}</span>
					      </div>
					    }
				        onConfirm={() => {
					      onDelete(r.id, r.name['en'])
					    }}
				        placement="left"
				      >
				        <Button
				          aria-label='Delete'
				          className="row-right"
				          type="default"
				          icon={<FontAwesomeIcon icon={['far', 'trash']} />}
				        >
				          {"Delete"}
				        </Button>
				      </Popconfirm>
			        </div>
			      </div>

			      <div className="row directory-attr-row">
				    { !isNationalHelplines && (
				      <div className="directory-attr">
				        <h3>{'Address:'}</h3>
					    <div className="content" dangerouslySetInnerHTML={{ __html: (r.address && r.address['en']) ? r.address['en'] : "" }}></div>
			          </div>
				    )}
				    { isNationalHelplines && (
				      <div className="directory-attr">
				        <h3>{'Hours:'}</h3>
					    <div className="content" dangerouslySetInnerHTML={{ __html: (r.hours && r.hours['en']) ? r.hours['en'] : "" }} />
				      </div>
				    )}
				    <div className="directory-attr">
			          <h3>{'Phone:'}</h3>
				      <div className="content"><p>{r.primaryPhoneNumber ? r.primaryPhoneNumber : ""}</p></div>
			        </div>
				    { isNationalHelplines && (
				      <div className="directory-attr">
				        <h3>{'Email:'}</h3>
					    <div className="content"><p>{r.primaryEmailAddress ? r.primaryEmailAddress : ""}</p></div>
			          </div>
				    )}
				    {  r.tags.iv?.length > 0 && (
				      <div className="directory-attr tags">
			            <h3>{'Filters:'}</h3>
					    <div className="content">
					      { r.tags.iv.map((tagId: string) => {
						    var tag = tagData?.filter(x => x.Id === tagId)[0];
						    if (tag) {
							  return (
								<>
								  { tag.Type.iv === "Location" &&
								    <IconTag 
								      key={"tag-" + tagId + "-" + r.id} 
									  text={tag.Title["en"]} 
									  icon={resolveIcon(tag.Icon?.iv)} 
							        />
								  }
								  { tag.Type.iv !== "Location" &&
							        <BoxTag
								      key={"tag-" + tagId + "-" + r.id}
							          text={tag.Title["en"]}
								      color={tag.ColorCode?.iv}
								      borderColor={tag.BorderColorCode?.iv}
						            />
								  }
								</>
							  );
						    }
						    return null;
					      })}
					    </div>
			          </div>
				    )}
			      </div>
			    </div>
			  </div>
            ))}
		  </div>
        </>
      }
      { !isLoading && editingDirectory !== null && selectedCountry &&
        <EditServiceDirectoryPage 
          onCancel={() => { setEditingDirectory(null); }}
          onSave={saveDirectory}
          directory={editingDirectory}
          country={selectedCountry}
		  tagOptions={tagOptions}
          isNationalHelplines={isNationalHelplines}
        />
      }
    </>
  );
}

export default ServiceDirectoryPage;

